import bystanderRules from "@content/rules/bystander.json";
import type { IndicatorAnswer, ResolvedRouting } from "../help/types";

type BystanderRules = {
  requiredModulesBeforeResources: string[];
  entryPaths: {
    infoOnly: { modules: string[]; resourceCategories: string[] };
    possibleMinor: { modules: string[]; resourceCategories: string[] };
  };
  observationFlow: {
    order: string[];
    onSkipAll: { modules: string[]; resourceCategories: string[] };
  };
  defaultAfterObservations: {
    modules: string[];
    resourceCategories: string[];
  };
  mappings: Array<{
    id: string;
    whenAnyYes: string[];
    modules: string[];
    resourceCategories: string[];
  }>;
};

const rules = bystanderRules as BystanderRules;

function unique(items: string[]): string[] {
  return [...new Set(items)];
}

function unionRouting(
  parts: Array<{ modules: string[]; resourceCategories: string[] }>,
): Omit<ResolvedRouting, "hasAnyYes"> {
  return {
    modules: unique(parts.flatMap((p) => p.modules)),
    resourceCategories: unique(parts.flatMap((p) => p.resourceCategories)),
  };
}

/** Observation ids in approved Item 5 bystander order. */
export function getObservationOrder(): string[] {
  return [...rules.observationFlow.order];
}

export function getRequiredModulesBeforeResources(): string[] {
  return [...rules.requiredModulesBeforeResources];
}

/**
 * Deterministic, invisible routing. Never returns scores or verdicts.
 * Only `yes` observations activate mappings. Assumptions never route.
 */
export function resolveConcernFromAnswers(
  answers: Record<string, IndicatorAnswer>,
): ResolvedRouting {
  const yesKeys = Object.entries(answers)
    .filter(([, value]) => value === "yes")
    .map(([key]) => key);

  if (yesKeys.length === 0) {
    const base = rules.defaultAfterObservations;
    return {
      modules: [...base.modules],
      resourceCategories: [...base.resourceCategories],
      hasAnyYes: false,
    };
  }

  const matched = rules.mappings.filter((map) =>
    map.whenAnyYes.some((id) => yesKeys.includes(id)),
  );

  const base = rules.defaultAfterObservations;
  if (matched.length === 0) {
    return {
      modules: [...base.modules],
      resourceCategories: [...base.resourceCategories],
      hasAnyYes: true,
    };
  }

  const unioned = unionRouting([
    { modules: base.modules, resourceCategories: base.resourceCategories },
    ...matched,
  ]);
  return { ...unioned, hasAnyYes: true };
}

export function resolveConcernInfoOnly(): ResolvedRouting {
  const path = rules.entryPaths.infoOnly;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveConcernSkipAll(): ResolvedRouting {
  const path = rules.observationFlow.onSkipAll;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

export function resolveConcernPossibleMinor(): ResolvedRouting {
  const path = rules.entryPaths.possibleMinor;
  return {
    modules: [...path.modules],
    resourceCategories: [...path.resourceCategories],
    hasAnyYes: false,
  };
}

/** Contact path: same categories as infoOnly (no separate contactNow in bystander rules). */
export function resolveConcernContactNow(): ResolvedRouting {
  return resolveConcernInfoOnly();
}
